// Santé des écritures de trésorerie (`oliva.finances`) et opérations sans verdict.
//
// DEUX causes de blocage, cumulables :
//   1. la base a REFUSÉ la dernière écriture de caisse (ou est inapte aux
//      transactions) — levée par une sonde de santé réussie ;
//   2. une commande est partie SANS VERDICT du serveur (audit C03) — levée
//      UNIQUEMENT par la réconciliation serveur de son `operation_id`. Une sonde
//      de santé verte ne prouve RIEN sur le sort de cette opération : elle ne
//      doit donc jamais lever ce blocage.
// Tant que l'une des deux subsiste, aucune écriture de caisse n'est acceptée :
// un bon ne doit JAMAIS passer « réglé » sans son mouvement de caisse en base.
import { useEffect, useState } from 'react';
import { santeFinances } from './store';

export const MESSAGE_BLOCAGE =
  'Trésorerie bloquée : la dernière écriture de caisse n’a pas été confirmée par la base (refus explicite ou résultat indéterminé). Aucun règlement n’est accepté tant que la base n’a pas été vérifiée.';

export const MESSAGE_EN_COURS =
  'Enregistrement en cours… la base n’a pas encore confirmé l’écriture de caisse. Ne cliquez pas une seconde fois.';

export const MESSAGE_INDETERMINE =
  'RÉSULTAT INDÉTERMINÉ : une opération de caisse est partie sans verdict de la base. NE LA RESSAISISSEZ PAS — « Vérifier la base » interroge le serveur sur son identifiant d’opération.';

// Le blocage survit au rechargement de la page : une panne de base ne doit pas être
// oubliée parce que l'opérateur a navigué. Clé hors `store.CLES` : jamais publiée.
const CLE = 'oliva.tresorerie.echec';
const memorise = (() => {
  try {
    return JSON.parse(localStorage.getItem(CLE) || 'null');
  } catch {
    return null;
  }
})();

let echec = memorise?.message
  ? { message: String(memorise.message), le: memorise.le ? new Date(memorise.le) : null }
  : null;
let suspens = []; // commandes sans verdict serveur (`financeCommandes`)
let envois = []; // commandes en vol : attente NORMALE d'une réponse, pas un incident
let controle = false;
let ecouteurs = [];

const construire = () => ({
  bloque: Boolean(echec) || suspens.length > 0,
  // Verrou d'écriture : une requête en cours interdit une seconde écriture de caisse
  // sans pour autant constituer un incident. Le bandeau rouge ne dépend que de `bloque`.
  verrou: Boolean(echec) || suspens.length > 0 || envois.length > 0,
  enCours: envois.length,
  envois,
  message: suspens.length
    ? `${MESSAGE_INDETERMINE} Opération(s) : ${suspens.map((s) => s.operation_id).join(', ')}.${
        echec ? ` Dernière cause : ${echec.message}` : ''
      }`
    : echec?.message ?? null,
  le: echec?.le ?? null,
  controle,
  indeterminees: suspens.length,
  operations: suspens,
});

let etat = construire();
const annoncer = () => {
  etat = construire();
  ecouteurs.forEach((f) => f(etat));
};

export const etatTresorerie = () => etat;
export const tresorerieBloquee = () => etat.bloque;

export const surTresorerie = (fn) => {
  ecouteurs.push(fn);
  fn(etat);
  return () => {
    ecouteurs = ecouteurs.filter((f) => f !== fn);
  };
};

export const signalerEchecTresorerie = (message) => {
  echec = { message: String(message ?? 'cause inconnue'), le: new Date() };
  controle = false;
  localStorage.setItem(CLE, JSON.stringify({ message: echec.message, le: echec.le.toISOString() }));
  annoncer();
};

export const signalerSuccesTresorerie = () => {
  if (!echec) return;
  echec = null;
  controle = false;
  localStorage.removeItem(CLE);
  annoncer();
};

// Liste des opérations au résultat indéterminé, publiée par `financeCommandes`.
// Elle commande à elle seule le blocage : seul le serveur peut la vider.
export const signalerIndeterminees = (liste) => {
  const rows = Array.isArray(liste) ? liste : [];
  if (rows.length === suspens.length && rows.every((r, i) => r.operation_id === suspens[i].operation_id)) return;
  suspens = rows;
  annoncer();
};

// Commandes dont la requête est en vol : elles verrouillent l'écriture le temps de
// la réponse, sans jamais être présentées comme un résultat indéterminé.
export const signalerEnvoisEnCours = (liste) => {
  const rows = Array.isArray(liste) ? liste.map(String) : [];
  if (rows.length === envois.length && rows.every((r, i) => r === envois[i])) return;
  envois = rows;
  annoncer();
};

// Contrôle explicite : rejoue le chemin transactionnel côté serveur avec une simple
// lecture. Le blocage n'est levé que si la base est réellement apte à écrire ET
// qu'aucune opération n'attend son verdict.
export async function verifierTresorerie() {
  controle = true;
  annoncer();
  try {
    const sante = await santeFinances();
    echec = null;
    controle = false;
    localStorage.removeItem(CLE);
    annoncer();
    return { ok: true, version: sante?.version ?? null, indeterminees: suspens.length };
  } catch (e) {
    const message = String(e?.message ?? e);
    echec = { message, le: new Date() };
    controle = false;
    localStorage.setItem(CLE, JSON.stringify({ message, le: echec.le.toISOString() }));
    annoncer();
    return { ok: false, message, indeterminees: suspens.length };
  }
}

export function useTresorerie() {
  const [v, setV] = useState(etat);
  useEffect(() => surTresorerie(setV), []);
  return v;
}

export const useTresorerieBloquee = () => useTresorerie().bloque;

// Verrou d'écriture + libellé adapté : « enregistrement en cours » n'est pas un blocage.
export function useTresorerieVerrou() {
  const t = useTresorerie();
  const enCours = t.enCours > 0;
  return {
    verrou: t.verrou,
    bloque: t.bloque,
    enCours,
    titre: enCours ? MESSAGE_EN_COURS : t.bloque ? MESSAGE_BLOCAGE : undefined,
  };
}
